import React, { useRef, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { LdFensterFuss } from '../ld/LdFenster';
import { LdSheet } from '../ui/LdSheet';
import { DiktatAktion, DiktatKind, DiktatModul, diktatStart, diktatAudio, diktatVerarbeiten, diktatAktionVerwerfen } from '../../lib/diktat-api';
import { useDiktatRecording } from '../../hooks/useDiktatRecording';
import { DiktatReview, DiktatReviewSteuerung } from './DiktatReview';
import { toast } from '../../lib/toast';
import { addToQueue, getQueueCount } from '../../lib/diktat-offline-db';
import { mikrofonAnfragen } from '../../lib/mikrofon';

type ModalPhase =
  | 'idle'
  | 'aufnahme'
  | 'transkribieren'
  | 'verarbeiten'
  | 'review'
  | 'fehler';

const MODUL_NAME: Record<DiktatModul, string> = {
  fehlzeit:   'Fehlzeit',
  mehrarbeit: 'Mehrarbeit',
  notiz:      'Notiz',
  termin:     'Termin',
  task:       'Aufgabe',
};

interface Props {
  open: boolean;
  onClose: () => void;
  streamPromise?: Promise<MediaStream> | null;
  fixedModul?: DiktatModul;
  kind?: DiktatKind | null;   // Welle P1b: „Über … diktieren“
}

const STATUS_TEXT: Partial<Record<ModalPhase, string>> = {
  transkribieren: 'Wird verschriftet …',
  verarbeiten:    'Vorschläge werden erstellt …',
};

export function UniversaldiktatModal({ open, onClose, streamPromise, fixedModul, kind = null }: Props) {
  const { dauer, startRecording, stopRecording, cleanup } = useDiktatRecording();

  const [phase,       setPhase]       = useState<ModalPhase>('idle');
  const [fehlerText,  setFehlerText]  = useState('');
  const [transcript,  setTranscript]  = useState<string | null>(null);
  const [aktionen,    setAktionen]    = useState<DiktatAktion[]>([]);
  const [offlineMode, setOfflineMode] = useState(false);
  const [offenAnzahl, setOffenAnzahl] = useState(0);
  const [alleLaeuft,  setAlleLaeuft]  = useState(false);
  const reviewRef = useRef<DiktatReviewSteuerung>(null);
  const aufnahmeIdRef = useRef<number | null>(null);
  const streamRef     = useRef<Promise<MediaStream> | null>(streamPromise ?? null);

  // Wenn Modal geschlossen wird: alles reset
  function handleClose() {
    cleanup();
    setPhase('idle');
    setFehlerText('');
    setTranscript(null);
    setAktionen([]);
    setOfflineMode(false);
    aufnahmeIdRef.current = null;
    streamRef.current = null;
    onClose();
  }

  // Pre-acquired stream bei jedem Open auffrischen
  const prevOpen = useRef(false);
  if (open && !prevOpen.current) {
    streamRef.current = streamPromise ?? null;
    prevOpen.current = true;
  }
  if (!open) prevOpen.current = false;

  async function starteAufnahme() {
    setFehlerText('');
    // Ohne vorab geholten Stream (Schnell-Diktat): Mikrofon noch im Tipp-Kontext anfragen,
    // bevor die Server-Registrierung awaited wird (iOS Safari verlangt die Nutzergeste).
    if (!streamRef.current) streamRef.current = mikrofonAnfragen();
    let serverAufnahmeId: number | null = null;

    // Server-Registrierung nur wenn online — sonst direkt Offline-Modus
    if (navigator.onLine) {
      try {
        const { id } = await diktatStart(
          fixedModul ? 'quick_mic' : 'global_fab',
          fixedModul,
          kind?.id ?? null,
        );
        serverAufnahmeId = id;
      } catch {
        // Server nicht erreichbar → Offline-Modus, Aufnahme trotzdem starten
        setOfflineMode(true);
      }
    } else {
      setOfflineMode(true);
    }

    aufnahmeIdRef.current = serverAufnahmeId;

    try {
      await startRecording(streamRef.current);
      streamRef.current = null;
      setPhase('aufnahme');
    } catch (err: any) {
      // Welle H6: abgelehnten Stream verwerfen — sonst nutzt „Erneut versuchen“ dasselbe
      // abgelehnte Promise und kann nie gelingen (auch nicht nach Freigabe)
      streamRef.current = null;
      setFehlerText(err?.message ?? 'Fehler beim Starten der Aufnahme.');
      setPhase('fehler');
    }
  }

  async function stoppeAufnahme() {
    setPhase('transkribieren');
    try {
      const { blob, ext } = await stopRecording();
      if (blob.size === 0) throw new Error('Aufnahme leer — bitte erneut versuchen.');

      // Offline-Fallback: Audio lokal speichern und Modal schließen
      if (offlineMode || !navigator.onLine) {
        await addToQueue({
          audioBlob:       blob,
          eingang:         fixedModul ? 'quick_mic' : 'global_fab',
          quick_mic_modul: fixedModul ?? null,
          schueler_id:     kind?.id ?? null,
          created_at:      new Date().toISOString(),
          sync_versuche:   0,
          letzter_versuch: null,
        });
        const n = await getQueueCount();
        toast(`Diktat gespeichert, wird hochgeladen, sobald du wieder online bist (${n} in Warteschlange)`);
        handleClose();
        return;
      }

      const aufnahmeId = aufnahmeIdRef.current!;
      try {
        const { transcript: text } = await diktatAudio(aufnahmeId, blob, ext);
        setTranscript(text);

        setPhase('verarbeiten');
        const { aktionen: erkannt } = await diktatVerarbeiten(aufnahmeId);
        setAktionen(erkannt);
        setPhase('review');
      } catch (apiErr: any) {
        if (!navigator.onLine) {
          // Netzwerk weg während Upload → Offline-Queue
          await addToQueue({
            audioBlob:       blob,
            eingang:         fixedModul ? 'quick_mic' : 'global_fab',
            quick_mic_modul: fixedModul ?? null,
            schueler_id:     kind?.id ?? null,
            created_at:      new Date().toISOString(),
            sync_versuche:   0,
            letzter_versuch: null,
          });
          const n = await getQueueCount();
          toast(`Verbindung unterbrochen — Diktat gespeichert für späteren Upload (${n} in Warteschlange)`);
          handleClose();
        } else {
          // Online-API-Fehler (Whisper/Claude) → Fehlermeldung anzeigen
          const msg = apiErr?.message ?? '';
          const display =
            msg.toLowerCase().includes('leer') || msg.toLowerCase().includes('kein text')
              ? 'Aufnahme zu leise — bitte deutlicher sprechen und erneut versuchen.'
              : msg.toLowerCase().includes('timeout') || msg.toLowerCase().includes('überschritten')
                ? 'KI braucht länger als erwartet — bitte erneut versuchen.'
                : msg || 'Verarbeitung fehlgeschlagen.';
          setFehlerText(display);
          setPhase('fehler');
        }
      }
    } catch (err: any) {
      setFehlerText(err?.message ?? 'Verarbeitung fehlgeschlagen.');
      setPhase('fehler');
    }
  }

  function onReviewComplete(bestaetigt: number, verworfen: number) {
    // Welle P1b: offene Ansichten (z. B. das Schülerprofil) laden nach
    if (bestaetigt > 0) window.dispatchEvent(new CustomEvent('ld-diktat-gespeichert', { detail: { schueler_id: kind?.id ?? null } }));
    toast(`${bestaetigt} ${bestaetigt !== 1 ? 'Vorschläge' : 'Vorschlag'} übernommen${verworfen > 0 ? `, ${verworfen} verworfen` : ''}`);
    handleClose();
  }

  // Welle K4: „Alle übernehmen“ — Karten mit offener Entscheidung bleiben stehen
  async function alleUebernehmen() {
    if (!reviewRef.current) return;
    setAlleLaeuft(true);
    try {
      const { offen } = await reviewRef.current.alleUebernehmen();
      if (offen > 0) toast(`${offen} ${offen === 1 ? 'Vorschlag braucht' : 'Vorschläge brauchen'} noch deine Entscheidung.`, 'info');
    } finally { setAlleLaeuft(false); }
  }

  // Welle K4: „Neu aufnehmen“ — die offenen Vorschläge dieser Aufnahme werden verworfen
  async function neuAufnehmen() {
    const stand = reviewRef.current?.stand();
    await Promise.all((stand?.offeneIds ?? []).map(id => diktatAktionVerwerfen(id).catch(() => {})));
    if (stand && stand.bestaetigt > 0) window.dispatchEvent(new CustomEvent('ld-diktat-gespeichert', { detail: { schueler_id: kind?.id ?? null } }));
    setAktionen([]); setTranscript(null); setFehlerText(''); setPhase('idle');
  }

  const isLoading = phase === 'transkribieren' || phase === 'verarbeiten';
  const titel = kind ? `Über ${kind.vorname} erzählen` : fixedModul ? `Diktat: ${MODUL_NAME[fixedModul]}` : 'Erzählen';
  const kontext = kind ? `Alles wird ${kind.name} zugeordnet. Du bestätigst jeden Vorschlag.`
    : fixedModul ? `Diktiere eine ${MODUL_NAME[fixedModul]}. Du bestätigst den Vorschlag.`
    : 'Sag, was passiert ist. Du bestätigst jeden Vorschlag.';

  const fuss = phase === 'review' ? (
    <LdFensterFuss onAbbrechen={handleClose} abbrechen="Schließen"
      haupt={offenAnzahl > 0 ? (offenAnzahl === 1 ? 'Übernehmen' : `Alle ${offenAnzahl} übernehmen`) : undefined}
      onHaupt={alleUebernehmen} laden={alleLaeuft} />
  ) : phase === 'fehler' ? (
    <LdFensterFuss onAbbrechen={handleClose} abbrechen="Schließen" haupt="Erneut versuchen" onHaupt={() => { setPhase('idle'); setFehlerText(''); }} />
  ) : undefined;

  return (
    <LdSheet
      open={open}
      onClose={isLoading ? () => {} : handleClose}
      maxWidth={620}
      kopf={{ titel, kontext, icon: 'mic' }}
      fuss={fuss}
    >
      {/* ── START ── */}
      {phase === 'idle' && (
        <div className="ld-dr-start">
          <button type="button" className="ld-dr-mic" onClick={starteAufnahme} aria-label="Aufnahme starten">
            <MIcon name="mic" size={38} />
          </button>
          <p>Tippe zum Starten. Du kannst mehreres auf einmal erzählen: Mitarbeit, Noten, Gespräche, Fehlzeiten, Notizen, Termine und Mehrarbeit.</p>
        </div>
      )}

      {/* ── AUFNAHME ── */}
      {phase === 'aufnahme' && (
        <div className="ld-dr-start">
          {offlineMode && <span className="ld-dr-hinweis">Ohne Verbindung, wird auf dem Gerät gespeichert</span>}
          <span className="ld-dr-zeit" aria-live="polite">Aufnahme läuft, {dauer} s</span>
          <div className="ld-dr-welle" aria-hidden="true">
            {[0.9, 1.1, 0.7, 1.3, 0.8, 1.2, 0.6, 1.0, 0.9, 1.1, 0.7, 1.2].map((d, i) => (
              <i key={i} style={{ animationDuration: `${d}s`, animationDelay: `${i * 0.06}s` }} />
            ))}
          </div>
          <button type="button" className="ld-dr-mic ist-aktiv" onClick={stoppeAufnahme} aria-label="Aufnahme beenden und auswerten">
            <MIcon name="stop" size={36} />
          </button>
          <p>Tippe zum Beenden. Danach siehst du die Vorschläge.</p>
        </div>
      )}

      {/* ── TRANSKRIBIEREN / VERARBEITEN ── */}
      {isLoading && (
        <div className="ld-dr-start" role="status">
          <div className="ld-dr-dreh" aria-hidden="true" />
          <p><b style={{ color: 'var(--ld-ink)' }}>{STATUS_TEXT[phase]}</b></p>
        </div>
      )}

      {/* ── REVIEW ── */}
      {phase === 'review' && (
        <DiktatReview
          ref={reviewRef}
          transcript={transcript}
          aktionen={fixedModul ? aktionen.filter(a => a.modul === fixedModul) : aktionen}
          onComplete={onReviewComplete}
          onOffen={setOffenAnzahl}
          onNeuAufnehmen={neuAufnehmen}
        />
      )}

      {/* ── FEHLER ── */}
      {phase === 'fehler' && (
        <div className="ld-dr-start" role="alert">
          <b style={{ fontSize: 15, color: 'var(--ld-ink)' }}>Das hat nicht geklappt</b>
          <p style={{ color: 'var(--ld-danger-text)' }}>{fehlerText}</p>
        </div>
      )}
    </LdSheet>
  );
}
